import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Box, Button, Chip, Container, List, ListItem, ListItemText, Paper, TextField, Typography, Alert, MenuItem } from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import { clientUpdateProfile } from "../../../store/thunks/clientAuthThunks";
import { SAT_CLIENT_FISCAL_REGIMES_ARRAY } from "hvp-shared";
import { CfdiUseSelect } from "../../../components/billing/CfdiUseSelect";
import { useEffect, useState } from "react";
import clientBillingApi from "../../../api/clientBillingApi";

interface LinkedQvetCustomer {
  qvetCustomerId: number;
  customerName: string;
  lastVisitDate: string | null;
}

const schema = z.object({
  businessName: z.string().min(3, "Mínimo 3 caracteres"),
  fiscalRegime: z.string().min(1, "Selecciona un régimen fiscal"),
  postalCode: z.string().length(5, "El código postal debe tener 5 dígitos"),
  cfdiUse: z.string().min(1, "Selecciona un uso de CFDI"),
  name: z.string().optional(),
});

type FormData = z.infer<typeof schema>;

export const ProfilePage = () => {
  const dispatch = useAppDispatch();
  const { client } = useAppSelector((state) => state.clientAuth);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [linkedCustomers, setLinkedCustomers] = useState<LinkedQvetCustomer[]>([]);
  const [loadingLinked, setLoadingLinked] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await clientBillingApi.get("/client-billing/qvet-customers");
        if (active) setLinkedCustomers(data?.data || []);
      } catch {
        if (active) setLinkedCustomers([]);
      } finally {
        if (active) setLoadingLinked(false);
      }
    })();
    return () => { active = false; };
  }, []);

  const formatDate = (iso: string | null) => {
    if (!iso) return "—";
    return new Date(iso).toLocaleDateString("es-MX", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const { control, handleSubmit, watch, formState: { errors, isSubmitting } } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      businessName: client?.businessName || "",
      fiscalRegime: client?.fiscalRegime || "",
      postalCode: client?.postalCode || "",
      cfdiUse: client?.cfdiUse || "",
      name: client?.name || "",
    },
  });

  const watchedRegime = watch("fiscalRegime");

  const onSubmit = async (data: FormData) => {
    try {
      setError(null);
      setSuccess(false);
      await dispatch(clientUpdateProfile(data) as any);
      setSuccess(true);
    } catch (err: any) {
      setError(err.message);
    }
  };

  return (
    <Container maxWidth="sm">
      <Paper elevation={2} sx={{ p: 4 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          Datos fiscales
        </Typography>

        {success && <Alert severity="success" sx={{ mb: 2 }}>Datos guardados correctamente</Alert>}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <TextField
          label="RFC"
          value={client?.rfc || ""}
          fullWidth
          margin="normal"
          disabled
          helperText="El RFC no se puede cambiar"
        />

        <form onSubmit={handleSubmit(onSubmit)}>
          <Controller name="businessName" control={control} render={({ field }) => (
            <TextField {...field} label="Razón social" fullWidth margin="normal" error={!!errors.businessName} helperText={errors.businessName?.message} />
          )} />

          <Controller name="fiscalRegime" control={control} render={({ field }) => (
            <TextField {...field} select label="Régimen fiscal" fullWidth margin="normal" error={!!errors.fiscalRegime} helperText={errors.fiscalRegime?.message}>
              {SAT_CLIENT_FISCAL_REGIMES_ARRAY.map((r) => <MenuItem key={r.code} value={r.code}>{r.label}</MenuItem>)}
            </TextField>
          )} />

          <Controller name="postalCode" control={control} render={({ field }) => (
            <TextField {...field} label="Código postal fiscal" fullWidth margin="normal" error={!!errors.postalCode} helperText={errors.postalCode?.message} />
          )} />

          <Controller name="cfdiUse" control={control} render={({ field }) => (
            <CfdiUseSelect
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              fiscalRegime={watchedRegime}
              rfc={client?.rfc}
              margin="normal"
              hint="Es tu uso habitual; al pedir una factura puedes elegir otro."
              error={!!errors.cfdiUse}
              errorMessage={errors.cfdiUse?.message}
            />
          )} />

          {/* The email is the key of the account (GH126): links to enter go there. It changes at reception */}
          <TextField
            label="Correo"
            value={client?.email || ""}
            fullWidth
            margin="normal"
            disabled
            helperText="Aquí recibes tus facturas y los enlaces para entrar. Para cambiarlo, pide ayuda en recepción."
          />

          <Controller name="name" control={control} render={({ field }) => (
            <TextField {...field} label="Nombre de contacto" fullWidth margin="normal" />
          )} />

          <Button type="submit" variant="contained" fullWidth size="large" disabled={isSubmitting} sx={{ mt: 3 }}>
            {isSubmitting ? "Guardando..." : "Guardar"}
          </Button>
        </form>
      </Paper>

      <Paper elevation={2} sx={{ p: 4, mt: 3 }}>
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          Clientes QVET vinculados
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Tu RFC se vincula automáticamente a los nombres que aparecen en QVET cuando facturas un ticket. Esto habilita ver tus tickets pendientes por facturar.
        </Typography>

        {loadingLinked ? (
          <Typography variant="body2" color="textSecondary">Cargando...</Typography>
        ) : linkedCustomers.length === 0 ? (
          <Alert severity="info">
            Aún no tienes clientes QVET vinculados. Se vincularán automáticamente cuando factures tu primer ticket.
          </Alert>
        ) : (
          <List dense disablePadding>
            {linkedCustomers.map((c) => (
              <ListItem key={c.qvetCustomerId} divider disableGutters>
                <ListItemText
                  primary={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                      <Typography variant="body1" fontWeight="medium">
                        {c.customerName || "(sin nombre en QVET)"}
                      </Typography>
                      <Chip label={`#${c.qvetCustomerId}`} size="small" variant="outlined" />
                    </Box>
                  }
                  secondary={`Última visita: ${formatDate(c.lastVisitDate)}`}
                />
              </ListItem>
            ))}
          </List>
        )}
      </Paper>
    </Container>
  );
};
